import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';

import {
  useSaveInstallationsMutation,
  SaveInstallations_Input,
  useSaveInstallationsFormQuery
} from 'generated/graphql';

import _ from 'lodash';

export type IFormState = {
  decisionDueDate: string;
  installationIds: string[];
};

const initialState: IFormState = {
  decisionDueDate: '',
  installationIds: []
};

export function useFormState(id: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      decisionDueDate: {
        label: 'Due date',
        type: 'date'
      },
      installationIds: {
        label: 'Installation ids',
        type: 'text'
      }
    }
  });

  const { data, called, loading } = useQuery(id);

  useEffect(() => {
    if (_.isEmpty(formState.installationIds) && id && called && !loading) {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'installationIds',
          value: data.installationIds
        }
      });
    }
    return () => {};
  }, [
    called,
    data.installationIds,
    dispatch,
    formState.installationIds,
    id,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveInstallations() {
  const [
    fnMutate,
    tuple
  ] = useSaveInstallationsMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveInstallations_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


export interface IData extends Pick<IFormState, 'installationIds'> {}

export function useQuery(projectId: string) {
  const tuple = useSaveInstallationsFormQuery({
    variables: {
      projectId: projectId
    },
    skip: projectId === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        installationIds: tuple.data?.project?.installations.map((node) => node.id) ?? []
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
